import { reatomAsync, withDataAtom } from "@reatom/async";
import { withAbort } from "@reatom/framework";
import { isAxiosError } from "axios";
import { withDebounce } from "src/common/pipes";
import { axiosErrorHandler } from "src/util";

import { API } from "../../api";

export interface BrandOption {
  label: string;
  value: number;
}

interface LoadBrandsAtomProps {
  query?: string;
  limit?: number;
  ids?: number[];
}

export const loadBrandsAtom = reatomAsync(
  async (
    ctx,
    { query, limit, ids }: LoadBrandsAtomProps,
  ): Promise<BrandOption[]> => {
    try {
      const response = await API.brandsApi.brandControllerGetBrands(
        {
          query,
          limit: limit ?? 200,
          ids,
        },
        { signal: ctx.controller.signal },
      );

      return response.data.data
        .sort((a, b) => ("" + a.name).localeCompare(b.name))
        .filter((brand) => brand.id !== undefined)
        .map((brand) => ({ label: brand.name, value: brand.id as number }));
    } catch (error) {
      if (isAxiosError(error)) {
        axiosErrorHandler(error);
      }
      return [];
    }
  },
  "loadBrandsAtom",
);

export const loadBrandsWithIdsAtom = reatomAsync(
  async (
    ctx,
    { query, limit, ids }: LoadBrandsAtomProps,
  ): Promise<BrandOption[]> => {
    const brandsByIds = await Promise.all([
      ids && ids.length > 0
        ? loadBrandsAtom(ctx, { limit, ids })
        : Promise.resolve([]),
      loadBrandsAtom(ctx, { query, limit }),
    ]);

    const map = new Map<number, BrandOption>();

    brandsByIds.forEach((brands) => {
      brands.forEach((brand) => {
        map.set(brand.value, brand);
      });
    });

    return Array.from(map.values());
  },
  "loadBrandsWithIdsAtom",
).pipe(withDataAtom([]), withAbort());

export const loadBrandsWithIdsDebounceAction = loadBrandsWithIdsAtom.pipe(
  withDebounce(400),
);
